import { CSSProperties, useEffect, useRef, useState } from 'react';
import { Browser } from '@capacitor/browser';
import SystemUpdateRoundedIcon from '@mui/icons-material/SystemUpdateRounded';
import Logo from 'assets/images/hjlogo.png';

/**
 * Player-facing copy. Voice matches the rest of the client's player copy (see
 * utils/unityStrings.ts): plain Title Case heading, the drawl in the body.
 *
 * On the wording specifically: the reason for the block is that WE shipped a
 * native build the installed one is too old to run. It is NOT that anything
 * changed on the player's device, which an earlier draft claimed and which is
 * both untrue and not something a player would care about either way.
 */
export const UPDATE_REQUIRED_TITLE = 'Update Required';
export const UPDATE_REQUIRED_MESSAGE =
  'Howdy, Partner! We’ve dealt out a fresh version of Hijack Poker, and the one yer holdin’ is too old to sit at the table. Grab the new one and you’ll be right back in the action.';
/** Shown only when the link could not be opened, never as the first thing. */
export const DOWNLOAD_FALLBACK_MESSAGE =
  'Couldn’t get ya there. Copy this link and open it in yer browser:';

type UpdateRequiredScreenProps = {
  /**
   * Where to get the new build. REQUIRED: this screen is terminal, so a
   * missing link would be a dead end. The gate guarantees one for every
   * native platform, which is why there is no empty-link state to render.
   */
  downloadUrl: string;
  /** Rendered small at the bottom so support can read back what they are on. */
  installed?: string;
  required?: string;
  /** Injected in tests and Storybook; defaults to the Capacitor browser. */
  openUrl?: (url: string) => Promise<unknown>;
};

/**
 * Terminal, non-dismissible screen shown when the installed native shell is
 * older than the published minimum (see services/nativeVersionGate).
 *
 * There is deliberately no "Later", no dismiss, and no route out: the app is
 * not expected to work on this shell, so letting the player past would drop
 * them into a lobby that fails in a less explicable way. This is also why it
 * is NOT rendered through SplashScreenContext — that gate is built around
 * reasons that get cleared, and this one never clears.
 *
 * The only action is the download link. If opening it fails, the screen
 * reveals the URL with a copy button rather than leaving a button that does
 * nothing.
 *
 * Nothing here says "store": Android is distributed as a direct APK from the
 * website, so store wording would be wrong on half the installs.
 */
const UpdateRequiredScreen = ({
  downloadUrl,
  installed,
  required,
  openUrl
}: UpdateRequiredScreenProps) => {
  const [failed, setFailed] = useState(false);
  const [copied, setCopied] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLButtonElement>(null);

  // The app is not rendered behind this screen, so there is nothing to trap
  // focus away from, but focus still starts wherever it was. Move it into the
  // dialog so a screen reader announces this instead of whatever was focused
  // before.
  //
  // The DIALOG, not the CTA. Chromium (and so the Android WebView) paints its
  // focus ring for a programmatic focus that follows no user interaction —
  // which is exactly this one, at cold start — while WebKit does not. Focusing
  // the button therefore drew a white ring around it on Android and nothing on
  // iOS, and since the ring paints outside the button's border box it was
  // sliced off at both ends by the scrolling column below. Focusing the
  // container (which carries outline: none, and is not a control anyone needs
  // a focus indicator for) leaves the CTA to show a ring only when a keyboard
  // user actually tabs to it, on both platforms.
  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  // When the fallback appears, the useful action changes: opening the link
  // just failed, so copying it is what the player needs next. Move focus
  // there rather than leaving it on the button that did not work.
  useEffect(() => {
    if (failed) copyRef.current?.focus();
  }, [failed]);

  const open = async () => {
    try {
      await (openUrl ? openUrl(downloadUrl) : Browser.open({ url: downloadUrl }));
    } catch {
      setFailed(true);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(downloadUrl);
      setCopied(true);
    } catch {
      // Clipboard can be unavailable in a WebView without a user-gesture
      // context; the URL is on screen and selectable either way.
      setCopied(false);
    }
  };

  return (
    <div
      ref={dialogRef}
      tabIndex={-1}
      style={containerStyle}
      role='alertdialog'
      aria-modal='true'
      aria-labelledby='update-required-title'
    >
      <div style={contentStyle}>
        <img src={Logo} alt='Hijack Poker' style={logoStyle} />

        <div style={iconWrapStyle}>
          <SystemUpdateRoundedIcon style={{ fontSize: '1.6rem' }} />
        </div>

        <h1 id='update-required-title' style={titleStyle}>
          {UPDATE_REQUIRED_TITLE}
        </h1>
        <p style={messageStyle}>{UPDATE_REQUIRED_MESSAGE}</p>

        <button type='button' onClick={open} style={ctaStyle}>
          Update now
        </button>

        {failed && (
          <div style={fallbackStyle} role='alert'>
            <span style={fallbackMessageStyle}>{DOWNLOAD_FALLBACK_MESSAGE}</span>
            <span style={urlStyle}>{downloadUrl}</span>
            <button ref={copyRef} type='button' onClick={copy} style={copyButtonStyle}>
              {copied ? 'Copied' : 'Copy link'}
            </button>
          </div>
        )}

        {installed && required && (
          <div style={versionsStyle}>
            <div style={versionRowStyle}>
              <span>Current version</span>
              <span style={versionValueStyle}>{installed}</span>
            </div>
            <div style={versionRowStyle}>
              <span>Minimum version</span>
              <span style={versionValueStyle}>{required}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpdateRequiredScreen;

const containerStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100vh',
  background: 'linear-gradient(180deg, #1c1a14 0%, #12100c 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '1.5rem',
  boxSizing: 'border-box',
  // Above every other overlay, including the splash: nothing may cover or
  // pre-empt a terminal state.
  zIndex: 100000,
  fontFamily: "'Outfit', sans-serif",
  // Takes focus on mount (see the effect above) purely to move it off whatever
  // was focused before. It is a container, not a control, so it gets no focus
  // indicator of its own.
  outline: 'none'
};

const contentStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '1rem',
  // Caps the column on tablets and keeps it inside a landscape phone.
  width: 'min(380px, 100%)',
  maxHeight: '100%',
  overflowY: 'auto',
  textAlign: 'center'
};

// maxHeight in vh so the logo gives way on a short viewport: on a landscape
// phone the stack is otherwise tall enough to push the CTA below the fold,
// and the one action on a screen with no way out should not need scrolling to
// reach.
const logoStyle: CSSProperties = {
  width: 'min(160px, 45vw)',
  maxHeight: '16vh',
  objectFit: 'contain'
};

const iconWrapStyle: CSSProperties = {
  width: '52px',
  height: '52px',
  borderRadius: '14px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(233, 140, 38, 0.16)',
  color: '#E98C26',
  flexShrink: 0
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: '1.35rem',
  fontWeight: 800,
  color: '#fff',
  letterSpacing: '0.01em'
};

const messageStyle: CSSProperties = {
  margin: 0,
  fontSize: '0.95rem',
  lineHeight: 1.5,
  color: 'rgba(255, 255, 255, 0.75)'
};

const ctaStyle: CSSProperties = {
  marginTop: '0.25rem',
  width: '100%',
  padding: '0.85rem 1.25rem',
  border: 'none',
  borderRadius: '10px',
  background: '#E98C26',
  color: '#12100c',
  fontFamily: "'Outfit', sans-serif",
  fontSize: '1rem',
  fontWeight: 800,
  letterSpacing: '0.02em',
  cursor: 'pointer',
  boxShadow: '0 0 18px rgba(233, 140, 38, 0.35)',
  // Draw the focus ring INSIDE the button. The button spans its column edge to
  // edge, and that column is a scroll container (overflow-y: auto forces
  // overflow-x to auto, which clips) — so a ring at the default offset is
  // sliced off at both ends and reads as a white sliver rather than a ring.
  // Only visible when a keyboard user tabs here; nothing paints otherwise.
  outlineOffset: '-3px'
};

const fallbackStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0.6rem',
  width: '100%',
  padding: '0.85rem',
  borderRadius: '10px',
  border: '1px solid rgba(239, 68, 68, 0.4)',
  background: 'rgba(239, 68, 68, 0.1)',
  boxSizing: 'border-box'
};

const fallbackMessageStyle: CSSProperties = {
  fontSize: '0.8rem',
  lineHeight: 1.4,
  color: 'rgba(255, 255, 255, 0.85)'
};

const urlStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: '#fbbf24',
  wordBreak: 'break-all',
  // Selectable, so a copy still works when the clipboard API is unavailable.
  userSelect: 'all'
};

const copyButtonStyle: CSSProperties = {
  padding: '0.45rem 0.9rem',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: '8px',
  background: 'rgba(255, 255, 255, 0.08)',
  color: '#fff',
  fontFamily: "'Outfit', sans-serif",
  fontSize: '0.8rem',
  fontWeight: 700,
  cursor: 'pointer'
};

// Labelled rows rather than one run-on line: this is the readout a player
// reads back to support, and "1.0.0" next to "1.4.0" means nothing without
// being told which is which.
const versionsStyle: CSSProperties = {
  marginTop: '0.25rem',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.2rem',
  fontSize: '0.72rem',
  letterSpacing: '0.02em',
  color: 'rgba(255, 255, 255, 0.4)'
};

const versionRowStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: '1rem'
};

const versionValueStyle: CSSProperties = {
  color: 'rgba(255, 255, 255, 0.65)',
  fontVariantNumeric: 'tabular-nums'
};
